export enum ReportType {
  CLIENT_EVENT_REPORT = "CLIENT_EVENT_REPORT",
  USER_REPORT = "USER_REPORT",
  PAYMENT_CHECK_REPORT = "PAYMENT_CHECK_REPORT",
  // Add more report types here as needed
}

export enum EventStatusEnum {
  NEW = "NEW",
  AWAITINGPAYMENT = "AWAITINGPAYMENT",
  COMPLETE = "COMPLETE",
  CANCELLED = "CANCELLED",
}

export interface ClientEventReportDTO {
  // Client Information
  clientId: string;
  clientName: string;
  companyName: string;
  contactEmail: string;
  contactPhone: string;

  // Event Information
  eventId: string;
  eventDate: string; // LocalDate from backend
  location: string;
  address: string;
  eventStatus: EventStatusEnum;

  // Event Details
  deliveryInfo: string;
  firmnessLevel: string;
  additionalInfo: string;
  dealerSpecialInstructions: string;

  // Pricing
  totalPrice: number;

  // Position/Table counts
  tableCounts: Record<string, number>;
  totalTables: number;

  // Timestamps
  createdDate: string; // OffsetDateTime from backend
  lastModifiedDate: string; // OffsetDateTime from backend

  // Event timing
  startTime: string; // OffsetDateTime from backend
  endTime: string; // OffsetDateTime from backend
}

export interface ClientEventReportFilter {
  eventStatuses?: EventStatusEnum[];
  eventDateFrom?: string; // ISO date string
  eventDateTo?: string; // ISO date string
  createdDateFrom?: string; // ISO date string
  createdDateTo?: string; // ISO date string
  clientIds?: string[];
  clientNameSearch?: string;
  companySearch?: string;
  locationSearch?: string;
}

// User Report Types (reusing existing types to avoid duplication)
export type UserStatus =
  "PENDING_APPROVAL" | "ACTIVE" | "INACTIVE" | "TERMINATED";

export enum SeniorityLevelEnum {
  LEVEL_1 = "LEVEL_1", // Display: 1
  LEVEL_2 = "LEVEL_2", // Display: 2
  LEVEL_3 = "LEVEL_3", // Display: 3
  LEVEL_4 = "LEVEL_4", // Display: 4
  LEVEL_5 = "LEVEL_5", // Display: 5
}

export interface UserReportDTO {
  fullName: string;
  email: string;
  phoneNumber: string;
  status: UserStatus;
  seniorityLevel: number;
  taxDocumentsSigned: boolean;
  badgeReceived: boolean;
  receivedShirt: boolean;
  shirtSize: string;
  city: string;
  positionEligibility: Record<string, boolean>;
}

export interface UserReportFilter {
  userStatuses?: UserStatus[];
  seniorityLevels?: SeniorityLevelEnum[]; // Using enum as per API documentation
  positionIds?: string[];
  nameSearch?: string;
  emailSearch?: string;
  taxDocumentsSigned?: boolean;
  badgeReceived?: boolean;
  receivedShirt?: boolean;
  shirtSizes?: string[];
  bilingual?: boolean;
  cities?: string[];
}

// Payment Check Report Types
export enum CheckStatus {
  CREATED = "CREATED",
  PAID = "PAID",
}

export enum RecipientType {
  EMPLOYEE = "EMPLOYEE",
  PARTNER = "PARTNER",
}

export interface PaymentCheckReportDTO {
  checkId: string;
  checkNumber: number;
  status: CheckStatus;
  issueDate: string | null; // ISO DateTime (nullable)
  createdDate: string; // ISO DateTime
  recipientId: string;
  recipientName: string;
  recipientType: RecipientType;
  totalAmount: number; // BigDecimal from backend
  paymentReference: string | null; // Nullable
  checkBatchId: string;
}

export interface PaymentCheckReportFilter {
  recipientNameSearch?: string;
  checkNumber?: number;
  issueDateFrom?: string; // ISO DateTime
  issueDateTo?: string; // ISO DateTime
  createdDateFrom?: string; // ISO DateTime
  createdDateTo?: string; // ISO DateTime
  statuses?: CheckStatus[];
}

export interface PaymentCheckReportResponse {
  content: PaymentCheckReportDTO[];
  page: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
}

export interface ReportOption {
  type: ReportType;
  title: string;
  description: string;
  icon?: string;
}

export const AVAILABLE_REPORTS: ReportOption[] = [
  {
    type: ReportType.CLIENT_EVENT_REPORT,
    title: "Client Event Report",
    description:
      "Generate detailed reports on client events including attendance, assignments, and performance metrics",
  },
  {
    type: ReportType.USER_REPORT,
    title: "User Report",
    description:
      "Export comprehensive user data with advanced filtering options including status, positions, certifications, and more",
  },
  {
    type: ReportType.PAYMENT_CHECK_REPORT,
    title: "Payment Check Report",
    description:
      "View and analyze recently issued payment checks with comprehensive filtering by recipient, amount, dates, and status",
  },
  // Add more reports here as they are implemented
];
